import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { cancelEditingGroup } from 'partner360/features/assortment/assortmentSlice'
import { IAssortmentGroup } from 'partner360/features/assortment/models/data/IAssortmentGroup'
import {
  selectAssortmentGroupsData,
  selectAssortmentItemsData,
} from 'partner360/features/assortment/selectors'
import {
  requestAssortmentGroups,
  requestAssortmentItems,
} from 'partner360/features/assortment/thunks'

import { applyAssortmentGroupChanges } from '../../../features/assortment/api/applyAssortmentGroupChanges'
import { applyAssortmentItemsChanges } from '../../../features/assortment/api/applyAssortmentItemsChanges'

interface IParams {
  fYear: number
  partnerId: string
  programType: ProgramTypes
  category: string
}

export const useSaveAssortmentData = ({ fYear, partnerId, programType, category }: IParams) => {
  const [isSaving, setIsSaving] = useState(false)
  const dispatch = useAppDispatch()
  const items = useSelector(selectAssortmentItemsData)
  const groups = useSelector(selectAssortmentGroupsData)

  const changedGroup = useMemo(() => {
    const [data] = groups.filter((it) => it.isChanged)
    return data
  }, [groups])

  const changedItems = useMemo(() => {
    return items.filter((it) => it.isChanged)
  }, [items])

  const saveAssortmentGroup = useCallback(async () => {
    const [newGroup] = await applyAssortmentGroupChanges(changedGroup)
    return {
      ...newGroup,
      PartnerId: newGroup.PartnerID || newGroup.PartnerId,
    }
  }, [changedGroup])

  const saveAssortmentItems = useCallback(
    async (group: IAssortmentGroup) => {
      const data = changedItems.map((it) => ({
        ...it,
        AssortmentGroupId: group.EntryId,
      }))
      // await Promise.all(data.map((item) => applyAssortmentItemChanges(item)))
      await applyAssortmentItemsChanges(data)
    },
    [changedItems]
  )

  const saveChanges = useCallback(async () => {
    setIsSaving(true)
    const group = await saveAssortmentGroup()
    await saveAssortmentItems(group)
    setIsSaving(false)
    dispatch(cancelEditingGroup())
    dispatch(
      requestAssortmentGroups({
        partnerId,
        programType,
        category,
        fYear,
      })
    )
    dispatch(
      requestAssortmentItems({
        partnerId,
        programType,
        category,
        fYear,
      })
    )
  }, [dispatch, saveAssortmentGroup, saveAssortmentItems, partnerId, fYear, programType, category])

  return {
    isSaving,
    saveChanges,
  }
}
